/**
 * ProactiveQuestions — AI-generated proactive questions for account intelligence.
 *
 * Displays questions that help sales reps capture missing intelligence:
 * - Organizational (org structure, decision makers)
 * - Competitive (incumbent tech, threats)
 * - Decision process (timeline, criteria)
 * - Relationship (engagement quality, champions)
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  HelpCircle, MessageSquare, Clock, X, ChevronDown, ChevronRight,
  Loader2, AlertCircle, Check, Bell, Building2, Swords, Users, Timer,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './ProactiveQuestions.css';

const CATEGORY_CONFIG = {
  organizational: {
    icon: Building2,
    label: 'Organizational',
    color: 'var(--color-primary)',
  },
  competitive: {
    icon: Swords,
    label: 'Competitive',
    color: 'var(--color-warning)',
  },
  decision_process: {
    icon: Timer,
    label: 'Decision Process',
    color: 'var(--color-success)',
  },
  timeline: {
    icon: Clock,
    label: 'Timeline',
    color: 'var(--color-info)',
  },
  relationship: {
    icon: Users,
    label: 'Relationship',
    color: 'var(--color-secondary)',
  },
};

const SEVERITY_CONFIG = {
  critical: { label: 'Critical', color: 'var(--color-error)' },
  high: { label: 'High', color: 'var(--color-warning)' },
  medium: { label: 'Medium', color: 'var(--color-text-muted)' },
  low: { label: 'Low', color: 'var(--color-text-muted)' },
};

function ProactiveQuestions({ accountId, accountName, collapsed = false }) {
  const queryClient = useQueryClient();
  const [isExpanded, setIsExpanded] = useState(!collapsed);
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [answerText, setAnswerText] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['account-intelligence', 'questions', accountId],
    queryFn: () => apiClient.get(`/api/account-intelligence/accounts/${accountId}/questions`),
    enabled: !!accountId,
    staleTime: 60_000,
  });

  // Optimistic (UI-responsiveness review 2026-08-08): answer/dismiss/snooze
  // clear the card the instant it's clicked — all three are removals from
  // the pending list. onMutate snapshots the cache and rolls back on error;
  // the invalidate on settle reconciles.
  const QK = ['account-intelligence', 'questions', accountId];
  const dropQuestion = async (questionId) => {
    await queryClient.cancelQueries({ queryKey: QK });
    const previous = queryClient.getQueryData(QK);
    queryClient.setQueryData(QK, (prev) => (
      prev && Array.isArray(prev.questions)
        ? { ...prev, questions: prev.questions.filter((q) => q.id !== questionId) }
        : prev
    ));
    return { previous };
  };
  const rollback = (_e, _v, ctx) => { if (ctx?.previous) queryClient.setQueryData(QK, ctx.previous); };
  const settle = () => queryClient.invalidateQueries({ queryKey: QK });

  const answerMutation = useMutation({
    mutationFn: ({ questionId, answer }) =>
      apiClient.post(`/api/account-intelligence/questions/${questionId}/answer`, { answer }),
    onMutate: ({ questionId, answer }) => {
      const question = activeQuestion;
      setActiveQuestion(null);
      setAnswerText('');
      return dropQuestion(questionId).then((ctx) => ({ ...ctx, question, answer }));
    },
    onError: (e, v, ctx) => {
      // Card comes back with the typed answer intact — text is never lost.
      rollback(e, v, ctx);
      if (ctx?.question) { setActiveQuestion(ctx.question); setAnswerText(ctx.answer || ''); }
    },
    onSettled: settle,
  });

  const dismissMutation = useMutation({
    mutationFn: ({ questionId, reason }) =>
      apiClient.post(`/api/account-intelligence/questions/${questionId}/dismiss`, { reason }),
    onMutate: ({ questionId }) => dropQuestion(questionId),
    onError: rollback,
    onSettled: settle,
  });

  const snoozeMutation = useMutation({
    mutationFn: ({ questionId, days }) =>
      apiClient.post(`/api/account-intelligence/questions/${questionId}/snooze`, { days }),
    onMutate: ({ questionId }) => dropQuestion(questionId),
    onError: rollback,
    onSettled: settle,
  });

  const questions = data?.questions || [];
  const pendingQuestions = questions.filter((q) => q.status === 'pending');

  const handleAnswer = (e) => {
    e.preventDefault();
    if (!answerText.trim() || !activeQuestion) return;
    answerMutation.mutate({ questionId: activeQuestion.id, answer: answerText.trim() });
  };

  if (!accountId) return null;

  return (
    <div className="pq-container">
      <button
        className="pq-header"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <div className="pq-header-left">
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <HelpCircle size={16} />
          <span className="pq-title">Proactive Questions</span>
          {pendingQuestions.length > 0 && (
            <span className="pq-badge">{pendingQuestions.length}</span>
          )}
        </div>
        {!isExpanded && pendingQuestions.length > 0 && (
          <span className="pq-preview">
            {pendingQuestions.length} question{pendingQuestions.length !== 1 ? 's' : ''} pending
          </span>
        )}
      </button>

      {isExpanded && (
        <div className="pq-body">
          {isLoading ? (
            <div className="pq-loading">
              <Loader2 size={16} className="pq-spinner" />
              <span>Generating questions...</span>
            </div>
          ) : error ? (
            <div className="pq-error">
              <AlertCircle size={14} />
              <span>Failed to load questions</span>
            </div>
          ) : pendingQuestions.length === 0 ? (
            <div className="pq-empty">
              <Check size={16} />
              <span>All caught up! No pending questions.</span>
            </div>
          ) : (
            <div className="pq-list">
              {pendingQuestions.map((question) => {
                const catConfig = CATEGORY_CONFIG[question.question_category] || CATEGORY_CONFIG.organizational;
                const sevConfig = SEVERITY_CONFIG[question.severity] || SEVERITY_CONFIG.medium;
                const CatIcon = catConfig.icon;
                const isActive = activeQuestion?.id === question.id;

                return (
                  <div
                    key={question.id}
                    className={`pq-card ${isActive ? 'pq-card-active' : ''}`}
                  >
                    <div className="pq-card-header">
                      <div className="pq-card-category" style={{ color: catConfig.color }}>
                        <CatIcon size={12} />
                        <span>{catConfig.label}</span>
                      </div>
                      {question.severity && question.severity !== 'medium' && (
                        <span
                          className="pq-severity"
                          style={{ color: sevConfig.color }}
                        >
                          {sevConfig.label}
                        </span>
                      )}
                    </div>

                    <p className="pq-question-text">{question.question_text}</p>

                    {question.trigger_reason && (
                      <p className="pq-trigger">{question.trigger_reason}</p>
                    )}

                    {isActive ? (
                      <form className="pq-answer-form" onSubmit={handleAnswer}>
                        <textarea
                          value={answerText}
                          onChange={(e) => setAnswerText(e.target.value)}
                          placeholder="Type your answer..."
                          rows={2}
                          autoFocus
                        />
                        <div className="pq-answer-actions">
                          <button
                            type="button"
                            className="pq-btn-cancel"
                            onClick={() => {
                              setActiveQuestion(null);
                              setAnswerText('');
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="pq-btn-submit"
                            disabled={!answerText.trim() || answerMutation.isPending}
                          >
                            {answerMutation.isPending ? (
                              <Loader2 size={12} className="pq-spinner" />
                            ) : (
                              <Check size={12} />
                            )}
                            Save
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className="pq-card-actions">
                        <button
                          className="pq-btn-answer"
                          onClick={() => setActiveQuestion(question)}
                        >
                          <MessageSquare size={12} />
                          Answer
                        </button>
                        <button
                          className="pq-btn-snooze"
                          onClick={() => snoozeMutation.mutate({ questionId: question.id, days: 7 })}
                          disabled={snoozeMutation.isPending}
                        >
                          <Bell size={12} />
                          Snooze
                        </button>
                        <button
                          className="pq-btn-dismiss"
                          onClick={() => dismissMutation.mutate({ questionId: question.id, reason: 'not_relevant' })}
                          disabled={dismissMutation.isPending}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ProactiveQuestions;
